import React, { useContext, useEffect, useId, useRef, useState } from 'react'
import { ReceivingNavigationContext } from './ReceivingNavigationContext'

import {
  ActionBar,
  ActionButton,
  ActionSheet,
  ContextBar,
  ContextPanel,
  ContextSummary,
  ContextToggle,
  ContextToggleLabel,
  HeaderButton,
  HeaderSpacer,
  HeaderTitle,
  RfHeader,
  RfMain,
  RfSurface,
  RfViewport,
  SheetAction,
  SheetBackdrop,
} from '../styles'

const containTab = (event: React.KeyboardEvent, root: HTMLElement | null) => {
  if (event.key !== 'Tab' || !root) return
  const controls = Array.from(
    root.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex="0"]'
    )
  )
  if (!controls.length) return
  const first = controls[0],
    last = controls[controls.length - 1]
  if (
    !root.contains(document.activeElement) ||
    (event.shiftKey ? document.activeElement === first : document.activeElement === last)
  ) {
    event.preventDefault()
    event.stopPropagation()
    ;(event.shiftKey ? last : first).focus()
  }
}

interface RfShellAction {
  label: string
  onSelect: () => void
  danger?: boolean
}

interface RfShellProps {
  title: string
  phase: string
  ariaLabel?: string
  embedded?: boolean
  contextSummary?: string
  contextDetails?: React.ReactNode
  children: React.ReactNode
  actions?: React.ReactNode
  overlay?: React.ReactNode
  moreActions?: RfShellAction[]
  onBack?: () => void
}

export const RfShell = ({
  title,
  phase,
  ariaLabel = 'RF workflow',
  embedded = false,
  contextSummary,
  contextDetails,
  children,
  actions,
  overlay,
  moreActions = [],
  onBack,
}: RfShellProps) => {
  const [contextOpen, setContextOpen] = useState(false)
  const returnToVariantMenu = useContext(ReceivingNavigationContext)
  const sheetActions: RfShellAction[] = returnToVariantMenu
    ? [{ label: 'Variant menu', onSelect: returnToVariantMenu }, ...moreActions]
    : moreActions
  const [sheetOpen, setSheetOpen] = useState(false)
  const [desktopPreviewLink, setDesktopPreviewLink] = useState(false)
  useEffect(() => {
    if (embedded || typeof window.matchMedia !== 'function') return
    const desktop = window.matchMedia('(min-width: 901px)')
    const update = () => setDesktopPreviewLink(desktop.matches)
    update()
    desktop.addEventListener('change', update)
    return () => desktop.removeEventListener('change', update)
  }, [embedded])
  const contextId = useId()
  const sheetTitleId = useId()
  const moreButtonRef = useRef<HTMLButtonElement | null>(null)
  const firstSheetActionRef = useRef<HTMLButtonElement | null>(null)
  const sheetHasOpenedRef = useRef(false)
  const overlayRef = useRef<HTMLDivElement>(null)
  const lastContentFocus = useRef<HTMLElement | null>(null)
  const overlayWasOpen = useRef(false)
  const overlayOpen = !!overlay
  useEffect(() => {
    if (overlayOpen) {
      const dialog = overlayRef.current?.querySelector<HTMLElement>('[role="dialog"]')
      if (dialog && !dialog.contains(document.activeElement))
        dialog.querySelector<HTMLElement>('button, input, select, textarea')?.focus()
    } else if (overlayWasOpen.current && lastContentFocus.current?.isConnected) {
      lastContentFocus.current.focus()
    }
    overlayWasOpen.current = overlayOpen
  }, [overlayOpen])

  useEffect(() => {
    if (!sheetOpen) {
      if (sheetHasOpenedRef.current) {
        moreButtonRef.current?.focus()
      }
      return
    }

    sheetHasOpenedRef.current = true
    firstSheetActionRef.current?.focus()

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSheetOpen(false)
      }
    }

    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [sheetOpen])

  return (
    <RfViewport $embedded={embedded}>
      <RfSurface $embedded={embedded} aria-label={ariaLabel}>
        <div
          style={{ display: 'contents' }}
          inert={sheetOpen || overlayOpen}
          onFocusCapture={(event) => {
            lastContentFocus.current = event.target as HTMLElement
          }}
        >
          <RfHeader>
            {onBack ? (
              <HeaderButton type="button" onClick={onBack}>
                Back
              </HeaderButton>
            ) : (
              <HeaderSpacer aria-hidden="true" />
            )}
            <HeaderTitle>
              {phase !== title ? <span>{phase}</span> : null}
              <strong>{title}</strong>
            </HeaderTitle>
            <HeaderButton ref={moreButtonRef} type="button" aria-haspopup="dialog" onClick={() => setSheetOpen(true)}>
              More
            </HeaderButton>
          </RfHeader>

          {contextSummary && contextDetails ? (
            <>
              <ContextToggle
                type="button"
                aria-expanded={contextOpen}
                aria-controls={contextId}
                onClick={() => setContextOpen((open) => !open)}
              >
                <ContextSummary>{contextSummary}</ContextSummary>
                <ContextToggleLabel>{contextOpen ? 'Hide' : 'Details'}</ContextToggleLabel>
              </ContextToggle>
              {contextOpen ? <ContextPanel id={contextId}>{contextDetails}</ContextPanel> : null}
            </>
          ) : contextSummary ? (
            <ContextBar>
              <ContextSummary>{contextSummary}</ContextSummary>
            </ContextBar>
          ) : null}

          <RfMain>{children}</RfMain>
          {actions ? <ActionBar>{actions}</ActionBar> : null}
        </div>

        {sheetOpen ? (
          <>
            <SheetBackdrop aria-hidden="true" onClick={() => setSheetOpen(false)} />
            <ActionSheet
              role="dialog"
              aria-modal="true"
              aria-labelledby={sheetTitleId}
              onKeyDownCapture={(event) => containTab(event, event.currentTarget)}
            >
              <h2 id={sheetTitleId}>More actions</h2>
              {sheetActions.map((action, index) => (
                <SheetAction
                  key={action.label}
                  ref={index === 0 ? firstSheetActionRef : undefined}
                  type="button"
                  $danger={action.danger}
                  onClick={() => {
                    setSheetOpen(false)
                    action.onSelect()
                  }}
                >
                  {action.label}
                </SheetAction>
              ))}
              {!embedded && desktopPreviewLink ? (
                <SheetAction
                  as="a"
                  href="/dev/rf-preview/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
                  onClick={() => setSheetOpen(false)}
                >
                  Developer preview
                </SheetAction>
              ) : null}
              <SheetAction
                ref={sheetActions.length === 0 ? firstSheetActionRef : undefined}
                type="button"
                onClick={() => setSheetOpen(false)}
              >
                Cancel
              </SheetAction>
            </ActionSheet>
          </>
        ) : null}
        <div
          ref={overlayRef}
          style={{ display: 'contents' }}
          onKeyDownCapture={(event) => {
            containTab(event, overlayRef.current)
            if (event.key === 'Escape' && onBack) {
              event.preventDefault()
              event.stopPropagation()
              onBack()
            }
          }}
        >
          {overlay}
        </div>
      </RfSurface>
    </RfViewport>
  )
}

export { ActionButton }
